<script setup>
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import Button from '@/components/forms/Button.vue';
import ModalDialog from '@/components/modals/ModalDialog.vue';
import BondDocumentModal from '@/components/modals/BondDocumentModal.vue';
import BondService from '@/services/BondService';
import {formatDate, showSuccessMessage} from '@/helper';

/**
 * IMA-517: záložka Dokumenty v detailu dluhopisu (administrace).
 *
 * Dokumenty k emisi jsou pro klienta povinné. Nová verze nahradí aktuální, starší verze zůstávají
 * v historii. Smazat jde celý dokument, nebo jednu verzi (poslední zbývající verzi ne).
 */
const {t} = useI18n();
const props = defineProps({
  bondId: {type: [String, Number], required: true},
});

const documents = ref([]);
const isLoading = ref(true);
// dokument, jehož historie verzí je rozbalená
const historyDocument = ref(null);
const versions = ref([]);
const versionInput = ref(null);
const versionTargetId = ref(null);

onMounted(fetchDocuments);

function fetchDocuments() {
  isLoading.value = true;
  BondService.getDocuments(props.bondId).then(response => {
    documents.value = response;
    isLoading.value = false;
    // po změně obnovit i otevřenou historii
    if (historyDocument.value) {
      const stillExists = documents.value.find(document => document.id === historyDocument.value.id);
      stillExists ? showHistory(stillExists) : (historyDocument.value = null);
    }
  });
}

function showHistory(document) {
  historyDocument.value = document;
  BondService.getVersions(props.bondId, document.id).then(response => {
    versions.value = response;
  });
}

function uploadDocument() {
  const {reveal, onConfirm} = createConfirmDialog(BondDocumentModal, {});
  reveal();
  onConfirm((data) => {
    BondService.createDocument(props.bondId, data).then(response => {
      showSuccessMessage(response.message);
      fetchDocuments();
    });
  });
}

function editDocument(document) {
  const {reveal, onConfirm} = createConfirmDialog(BondDocumentModal, {document});
  reveal();
  onConfirm((data) => {
    BondService.updateDocument(props.bondId, document.id, data).then(response => {
      showSuccessMessage(response.message);
      fetchDocuments();
    });
  });
}

/** Výběr souboru pro novou verzi přes skrytý input (jeden pro všechny řádky). */
function chooseVersionFile(document) {
  versionTargetId.value = document.id;
  versionInput.value.value = '';
  versionInput.value.click();
}

function onVersionFileChange(event) {
  const file = event.target.files[0];
  if (!file || !versionTargetId.value) {
    return;
  }
  BondService.addVersion(props.bondId, versionTargetId.value, file).then(response => {
    showSuccessMessage(response.message);
    fetchDocuments();
  });
}

function deleteDocument(document) {
  const {reveal, onConfirm} = createConfirmDialog(ModalDialog, {
    title: t('bondDocuments.delete_document'),
    message: t('bondDocuments.delete_document_confirm', {name: document.name}),
  });
  onConfirm(() => {
    BondService.deleteDocument(props.bondId, document.id).then(response => {
      showSuccessMessage(response.message);
      fetchDocuments();
    });
  });
  reveal();
}

function deleteVersion(version) {
  const {reveal, onConfirm} = createConfirmDialog(ModalDialog, {
    title: t('bondDocuments.delete_version'),
    message: t('bondDocuments.delete_version_confirm', {version: version.version}),
  });
  onConfirm(() => {
    BondService.deleteVersion(props.bondId, historyDocument.value.id, version.media_id).then(response => {
      showSuccessMessage(response.message);
      fetchDocuments();
    });
  });
  reveal();
}

const formatDateTime = value => (value ? formatDate(value, 'd.m.Y H:i') : '–');

// tabulky jako ve vzoru (vue-good-table se stránkováním); data jsou celá v paměti, řadí a stránkuje tabulka
const paginationOptions = {
  enabled: true,
  perPage: 10,
  perPageDropdown: [10, 20],
  dropdownAllowAll: false,
  rowsPerPageLabel: t('strategies.entriesPerPage'),
  nextLabel: t('strategies.next'),
  prevLabel: t('strategies.previous'),
  ofLabel: t('strategies.of'),
};

const documentColumns = [
  {label: t('bondDocuments.name'), field: 'name'},
  {label: t('bondDocuments.type'), field: 'bond_document_type', sortable: false},
  {label: t('bondDocuments.version'), field: 'version', type: 'number'},
  {label: t('bondDocuments.valid_from'), field: 'valid_from'},
  {label: t('bondDocuments.attach_to_client_short'), field: 'attach_to_client', sortable: false},
  {label: '', field: 'actions', sortable: false, tdClass: 'text-end text-nowrap'},
];

const versionColumns = [
  {label: t('bondDocuments.version'), field: 'version', type: 'number'},
  {label: t('bondDocuments.valid_from'), field: 'valid_from'},
  {label: t('bondDocuments.valid_to'), field: 'valid_to'},
  {label: t('bondDocuments.uploaded_by'), field: 'uploaded_by_name', sortable: false},
  {label: t('bondDocuments.accepted_count'), field: 'accepted_count', type: 'number'},
  {label: '', field: 'actions', sortable: false, tdClass: 'text-end text-nowrap'},
];
</script>

<template>
  <!-- Dokumenty k emisi jako ve vzoru: tabulka, pod ní poznámka a nahrání dokumentu -->
  <input ref="versionInput" type="file" accept="application/pdf,.pdf" class="d-none" @change="onVersionFileChange"/>
  <vue-good-table
      :columns="documentColumns"
      :rows="documents"
      :pagination-options="paginationOptions">
    <template #table-row="props">
      <span v-if="props.column.field === 'name'">
        <a v-if="props.row.file" :href="props.row.file.download_url" target="_blank">{{ props.row.name }}</a><span v-else>{{ props.row.name }}</span>
      </span>
      <span v-else-if="props.column.field === 'bond_document_type'">{{ props.row.bond_document_type ? t('bondDocuments.types.' + props.row.bond_document_type) : '–' }}</span>
      <span v-else-if="props.column.field === 'valid_from'">{{ formatDateTime(props.row.valid_from) }}</span>
      <span v-else-if="props.column.field === 'attach_to_client'">{{ props.row.attach_to_client ? t('strategies.yes') : t('strategies.no') }}</span>
      <span v-else-if="props.column.field === 'actions'">
        <button type="button" class="btn btn-link" :title="t('bondDocuments.upload_version')" :aria-label="t('bondDocuments.upload_version')" @click="chooseVersionFile(props.row)"><i class="mdi mdi-upload"/></button>
        <button type="button" class="btn btn-link" :title="t('bondDocuments.history')" :aria-label="t('bondDocuments.history')" @click="showHistory(props.row)"><i class="mdi mdi-history"/></button>
        <button type="button" class="btn btn-link" :title="t('bondDocuments.edit_document')" :aria-label="t('bondDocuments.edit_document')" @click="editDocument(props.row)"><i class="mdi mdi-pencil"/></button>
        <button type="button" class="btn btn-link" :title="t('bondDocuments.delete_document')" :aria-label="t('bondDocuments.delete_document')" @click="deleteDocument(props.row)"><i class="mdi mdi-delete"/></button>
      </span>
      <span v-else>{{ props.formattedRow[props.column.field] }}</span>
    </template>
    <template v-slot:emptystate>
      <div class="text-center">{{ isLoading ? t('strategies.data_loading') : t('bondDocuments.no_documents') }}</div>
    </template>
  </vue-good-table>
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3 mb-4">
    <span class="small text-muted">{{ t('bondDocuments.hint') }}</span>
    <Button :label="t('bondDocuments.upload_document')" icon="mdi-file-upload" @click="uploadDocument()"/>
  </div>

  <!-- Historie verzí vybraného dokumentu – samostatná sekce jako ve vzoru -->
  <div v-if="historyDocument" class="page-section mt-4">
    <div class="page-section__heading">
      <div class="d-flex align-items-start justify-content-between">
        <div>
          <div class="page-section__title">{{ t('bondDocuments.history_of', {name: historyDocument.name}) }}</div>
          <div class="page-section__subtitle">{{ t('bondDocuments.history_hint') }}</div>
        </div>
        <button type="button" class="btn btn-link" :aria-label="t('bondDocuments.close')" @click="historyDocument = null"><i class="mdi mdi-close"/></button>
      </div>
    </div>
    <div class="page-section__content">
      <vue-good-table
          :columns="versionColumns"
          :rows="versions"
          :pagination-options="paginationOptions">
        <template #table-row="props">
          <span v-if="props.column.field === 'version'">{{ props.row.version }} <span v-if="props.row.is_current" class="badge bg-success ms-1">{{ t('bondDocuments.current') }}</span></span>
          <span v-else-if="props.column.field === 'valid_from'">{{ formatDateTime(props.row.valid_from) }}</span>
          <span v-else-if="props.column.field === 'valid_to'">{{ formatDateTime(props.row.valid_to) }}</span>
          <span v-else-if="props.column.field === 'uploaded_by_name'">{{ props.row.uploaded_by_name ?? '–' }}</span>
          <span v-else-if="props.column.field === 'actions'">
            <a :href="props.row.download_url" target="_blank" class="btn btn-link" :title="t('bondDocuments.download')" :aria-label="t('bondDocuments.download')"><i class="mdi mdi-download"/></a>
            <!-- poslední zbývající verzi samostatně smazat nejde (BE vrací 422), proto tlačítko skryté -->
            <button v-if="versions.length > 1" type="button" class="btn btn-link" :title="t('bondDocuments.delete_version')" :aria-label="t('bondDocuments.delete_version')" @click="deleteVersion(props.row)"><i class="mdi mdi-delete"/></button>
          </span>
          <span v-else>{{ props.formattedRow[props.column.field] }}</span>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>
